/* eslint-disable max-lines */
import React from 'react';
import { useSelector } from 'react-redux';
import { Redirect, Switch } from 'react-router';

import { loadable } from './common/loadable';
import ApplicationsPageRedirect from './modules/EmployerATS/ApplicationsPageRedirect';
import { getIsChatEnabled } from './modules/GlintsChat/selectors';
import JobCopyRedirection from './modules/JobV2/JobCopyRedirection';
import { retry } from './retry';
import { authRoutes } from './routes/authRoutes';
import {
  RedirectApplicationHistoryToLegacy,
  RedirectCandidatesToLegacy,
} from './routes/CandidatePathAliasRedirects';
import MissingPageRedirect from './routes/MissingPageRedirect';
import Route from './routes/Route';
import { getIsJobCopyDisabled } from './selectors/jobs';

const CompanyEditPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-company-edit" */ './modules/CompanyEdit/CompanyEdit'
      )
  )
);

const CompanyTeamPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-company-team" */ './modules/CompanyTeam/CompanyTeamPage'
      )
  )
);

const DashboardPage = loadable(() =>
  retry(
    () => import(/* webpackChunkName: "page-dashboard" */ './modules/Dashboard')
  )
);

const Error403Page = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-error-403" */ './modules/ErrorPages/Error403'
      )
  )
);

const Error404Page = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-error-404" */ './modules/ErrorPages/Error404'
      )
  )
);

const Error500Page = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-error-500" */ './modules/ErrorPages/Error500'
      )
  )
);

const JobDraftRedirection = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-job-draft-redirection" */ './modules/JobV2/JobDraftRedirection'
      )
  )
);

const JobPostingPageV2 = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-job-posting-v2" */ './modules/JobV2/JobPostingPage'
      )
  )
);

const JobEditPageV2 = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-job-edit-v2" */ './modules/JobV2/JobEditPage'
      )
  )
);

const JobEnhancePage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-job-enhance" */ './modules/JobV2/components/JobEnhancePage/JobEnhancePage'
      )
  )
);

const TermsOfUsePage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-terms-of-use" */ './modules/TermsOfUse/TermsOfUse'
      )
  )
);

const CompanyCreationPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-company-creation" */ './modules/CompanyOnboarding/CompanyCreationPage/CompanyCreationPage'
      )
  )
);

const PrivacyPolicyPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-terms-of-use" */ './modules/PrivacyPolicy/PrivacyPolicy'
      )
  )
);

const ResumePage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-resume" */ './modules/ResumePage/ResumePage'
      )
  )
);

const SignUpPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-signup" */ './modules/CompanyOnboarding/SignUpPage/SignUpPage'
      )
  )
);

const RedirectCandidatesPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-redirect-candidates" */ './modules/RedirectCandidatesPage/RedirectCandidatesPage'
      )
  )
);

const SettingsPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-settings" */ './modules/Settings/SettingsPage'
      )
  )
);

const EmployerATSPageLegacy = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-employer-ats-v2" */ './modules/EmployerATSV2'
      )
  )
);

const ApplicationHistoryPageLegacy = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "application-history-v2" */ './modules/EmployerATSV2/ApplicationHistory/ApplicationHistoryPage'
      )
  )
);

const ChatPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "glints-chat-page" */ './modules/GlintsChat/ChatPage'
      )
  )
);

const ApplicationDetailsPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "page-job-posting-details" */ './modules/ApplicationDetails/ApplicationDetailsPage'
      )
  )
);

const SubscriptionPlanUpgradePage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "subscription-plan-upgrade" */ './modules/EmployerCommerce/SubscriptionPlanUpgrade/SubscriptionPlanUpgradePage'
      )
  ).then(module => module.SubscriptionPlanUpgradePage)
);

const TalentSearchPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "talent-search" */ './modules/TalentSearch/TalentSearch'
      )
  )
);
const CompanyLocationUpdatePage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "company-location-update" */ './modules/CompanyLocationUpdate/CompanyLocationUpdate'
      )
  ).then(module => module.CompanyLocationUpdatePage)
);
const CompanyReportStatusPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "company-report-status" */ './modules/CompanyReportStatus/CompanyReportStatusPage'
      )
  ).then(module => module.CompanyReportStatusPage)
);

const FeaturesPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "features-page" */ './modules/CreditSystem/Features/FeaturesPage'
      )
  )
);
const FeaturesHistoryPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "features-history-page" */ './modules/CreditSystem/FeaturesHistory/FeaturesHistoryPage'
      )
  )
);
const FeaturesOrderHistoryPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "features-order-history-page" */ './modules/CreditSystem/FeaturesOrderHistory/FeaturesOrderHistoryPage'
      )
  )
);
const FeaturesCreditHistoryPage = loadable(() =>
  retry(() =>
    import(
      /* webpackChunkName: "features-credit-history-page" */ './modules/CreditSystem/FeaturesOrderHistory/FeaturesOrderHistoryPage'
    ).then(m => m.FeatureCreditHistoryPage)
  )
);

const CreditPurchaseConfirmationPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "credit-purchase-confirmation" */ './modules/CreditSystem/CreditPurchaseConfirmation/CreditPurchaseConfirmationPage'
      )
  )
);

const JobMetricsPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "job-metrics" */ './modules/JobMetrics/JobMetricsPage'
      )
  )
);

const LandingPageBasedOnCountry = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "landing-page-based-on-country" */ './modules/Landing/LandingPageBasedOnCountry'
      )
  )
);
const HireLandingPage = loadable(() =>
  retry(
    () =>
      import(
        /* webpackChunkName: "hire-landing-page" */ './modules/Landing/HireLandingPage/HireLandingPage'
      )
  )
);

const Routes = () => {
  const empCanAccessMessaging = useSelector(getIsChatEnabled);
  const isJobCopyDisabled = useSelector(getIsJobCopyDisabled);
  return (
    <Switch>
      <Route
        path="/"
        component={LandingPageBasedOnCountry}
        allowUnverifiedUserEmail={true}
        exact={true}
        glintsTrackingPageName={null}
      />
      <Route
        path="/hire"
        component={HireLandingPage}
        allowUnverifiedUserEmail={true}
        exact={true}
        glintsTrackingPageName={'hire_landing_page'}
      />

      {authRoutes.map(authRoute => (
        <Route
          key={authRoute.path}
          path={authRoute.path}
          component={authRoute.component}
          allowUnverifiedUserEmail={authRoute.allowUnverified}
          glintsTrackingPageName={authRoute.glintsTrackingPageName}
          trackPageView={authRoute.trackPageView}
        />
      ))}

      <Route
        path="/onboarding"
        index={false}
        component={CompanyCreationPage}
        mustBeAuthenticated={true}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="company_creation"
      />
      {/* Keep API-generated /employers-onboarding links working without the
          production redirect used by loker.glints.com. */}
      <Route
        path="/employers-onboarding"
        index={false}
        component={CompanyCreationPage}
        mustBeAuthenticated={true}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="company_creation"
      />
      <Route
        path="/privacy-policy"
        index={false}
        component={PrivacyPolicyPage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="privacy_policy"
      />
      <Route
        path="/signup"
        index={false}
        component={SignUpPage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="signup"
      />
      <Route
        path="/job/create"
        mustBeAuthenticated={true}
        index={false}
        component={JobDraftRedirection}
        exact={true}
        desktopOnly={true}
        glintsTrackingPageName={null}
      />
      <Route
        path="/job/edit/:jobId"
        mustBeAuthenticated={true}
        index={false}
        component={JobEditPageV2}
        desktopOnly={true}
        glintsTrackingPageName="edit_job"
      />
      <Route
        path="/job/draft/:draftId"
        mustBeAuthenticated={true}
        index={false}
        component={JobPostingPageV2}
        exact={true}
        desktopOnly={true}
        glintsTrackingPageName="post_job"
      />
      <Route
        path="/job/enhance/:jobId"
        mustBeAuthenticated={true}
        index={false}
        component={JobEnhancePage}
        exact={true}
        desktopOnly={true}
        glintsTrackingPageName="enhance_job"
        trackPageView={false}
      />
      {!isJobCopyDisabled && (
        <Route
          path="/job/copy/:jobId"
          mustBeAuthenticated={true}
          index={false}
          component={JobCopyRedirection}
          desktopOnly={true}
          glintsTrackingPageName={null}
        />
      )}

      <Route
        path="/dashboard"
        exact={true}
        mustBeAuthenticated={true}
        index={false}
        component={DashboardPage}
        desktopOnly={true}
        glintsTrackingPageName="dashboard"
      />
      <Route
        path="/talent-search"
        component={TalentSearchPage}
        mustBeAuthenticated={true}
        desktopOnly={true}
        glintsTrackingPageName="talent_search"
      />
      <Redirect path="/recommended-talents" to="/dashboard" />
      {
        // TODO: @afzal remove this page, not use anymore
      }
      <Route
        path="/review/:jid/candidates"
        component={ApplicationsPageRedirect}
        desktopOnly={true}
        mustBeAuthenticated={true}
        glintsTrackingPageName={null}
      />
      <Redirect path="/review" to={'/dashboard'} exact={true} />
      <Route
        path="/job-metrics"
        mustBeAuthenticated={true}
        allowUnverifiedCompany={false}
        desktopOnly={true}
        index={false}
        component={JobMetricsPage}
        glintsTrackingPageName="job_metrics"
      />
      <Route
        path="/candidates/application-history"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={RedirectApplicationHistoryToLegacy}
        glintsTrackingPageName={null}
      />
      <Route
        path="/candidates"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={RedirectCandidatesToLegacy}
        glintsTrackingPageName={null}
      />
      <Route
        path="/application-history"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={ApplicationHistoryPageLegacy}
        glintsTrackingPageName="application_history"
      />
      <Route
        path="/manage-candidates"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={EmployerATSPageLegacy}
        glintsTrackingPageName="manage_candidates"
      />
      <Redirect path="/ats" to="/manage-candidates" />
      {empCanAccessMessaging && (
        <Route
          path="/messages"
          mustBeAuthenticated={true}
          desktopOnly={true}
          index={false}
          allowUnverifiedCompany={false}
          component={ChatPage}
          glintsTrackingPageName="chats"
        />
      )}

      <Route
        path="/companies/:companyId/edit"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={CompanyEditPage}
        glintsTrackingPageName="company_edit"
      />
      <Route
        path="/company/edit"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={CompanyEditPage}
        glintsTrackingPageName="company_edit"
      />
      <Route
        path="/company/team"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={CompanyTeamPage}
        glintsTrackingPageName="company_team"
      />
      <Redirect path="/group" exact={true} to="/dashboard" />
      <Route
        path="/settings"
        exact={true}
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={SettingsPage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="settings"
      />
      <Redirect path="/settings/rejection" to="/settings/templateMessages" />
      <Route
        path="/settings/:tab"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={SettingsPage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="settings"
      />
      <Route
        path="/redirect"
        component={RedirectCandidatesPage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName={null}
      />
      <Route
        path="/terms"
        component={TermsOfUsePage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="terms"
      />
      {
        // TODO: @afzal remove this page, not use anymore
      }
      <Route
        path="/resume/:jobId/application/:applicationId"
        mustBeAuthenticated={true}
        desktopOnly={true}
        index={false}
        component={ResumePage}
        glintsTrackingPageName={null}
      />
      <Route
        path="/jobs/:jobId/applications/:applicationId"
        index={false}
        component={ApplicationDetailsPage}
        desktopOnly={true}
        mustBeAuthenticated={true}
        glintsTrackingPageName="application_detail"
      />
      <Route
        path="/upgrade-plan"
        index={false}
        desktopOnly={true}
        component={SubscriptionPlanUpgradePage}
        mustBeAuthenticated={true}
        glintsTrackingPageName="upgrade_plan"
      />
      <Route
        path="/features"
        index={false}
        desktopOnly={true}
        exact={true}
        component={FeaturesPage}
        mustBeAuthenticated={true}
        glintsTrackingPageName="features"
      />
      <Route
        path="/features/history"
        index={false}
        exact={true}
        component={FeaturesHistoryPage}
        mustBeAuthenticated={true}
        desktopOnly={true}
        glintsTrackingPageName="features_history"
      />
      {/* TODO: FIXME: Remove this route once we fix the order history tab */}
      <Route
        path="/features/order-history"
        index={false}
        exact={true}
        component={FeaturesOrderHistoryPage}
        mustBeAuthenticated={true}
        desktopOnly={true}
        glintsTrackingPageName={null}
      />
      <Route
        path="/features/credit-history"
        index={false}
        exact={true}
        component={FeaturesCreditHistoryPage}
        mustBeAuthenticated={true}
        desktopOnly={true}
        glintsTrackingPageName="features_credit_history"
      />
      <Route
        path="/credit-confirmation"
        index={false}
        exact={true}
        component={CreditPurchaseConfirmationPage}
        mustBeAuthenticated={true}
        desktopOnly={true}
        glintsTrackingPageName="credit_confirmation"
      />
      <Route
        path="/company-location"
        index={false}
        component={CompanyLocationUpdatePage}
        mustBeAuthenticated={true}
        desktopOnly={true}
        glintsTrackingPageName="company_location"
      />
      <Route
        path="/company-report-status"
        index={false}
        component={CompanyReportStatusPage}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="company_report_status"
      />
      <Route
        path="/403"
        component={Error403Page}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="error_403"
      />
      <Route
        path="/404"
        component={Error404Page}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="error_404"
      />
      <Route
        path="/500"
        component={Error500Page}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName="error_500"
      />
      <Route
        path="*"
        component={MissingPageRedirect}
        allowUnverifiedUserEmail={true}
        glintsTrackingPageName={null}
      />
    </Switch>
  );
};

export default Routes;
